import { useEffect, type RefObject } from "react";

interface UsePopoverDismissOptions {
  open: boolean;
  onClose: () => void;
  anchorRef: RefObject<HTMLElement | null>;
  contentRef: RefObject<HTMLElement | null>;
  stopEscapePropagation?: boolean;
}

/**
 * Unified popover dismissal: while open, a capture-phase pointerdown outside
 * BOTH anchorRef and contentRef calls onClose; Escape calls onClose.
 *
 * The anchor exemption prevents close-then-reopen double-fire when the toggle
 * button itself is clicked (the button's own click handler re-opens).
 *
 * No listeners are attached while closed; all are cleaned up on close/unmount.
 */
export function usePopoverDismiss({
  open,
  onClose,
  anchorRef,
  contentRef,
  stopEscapePropagation = false,
}: UsePopoverDismissOptions): void {
  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (
        anchorRef.current?.contains(target) ||
        contentRef.current?.contains(target)
      ) {
        return;
      }
      onClose();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (stopEscapePropagation) e.stopPropagation();
        onClose();
      }
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose, anchorRef, contentRef, stopEscapePropagation]);
}
